/**
 * QuestionnaireEditor — Batch #14.242
 *
 * Éditeur d'un questionnaire à remplir + signer : titre, entreprise (page couverture),
 * sections « Questions » (le client répond) ou « Texte » (à lire, ex. confidentialité),
 * modèles réutilisables. Composant contrôlé : value / onChange.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, FolderOpen, ListChecks, AlignLeft } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


const inputCls = "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm";
export const QUESTION_TYPES = [
  ["short", "Texte court"],
  ["long", "Texte long"],
  ["yesno", "Oui / Non"],
  ["choice", "Choix (une option)"],
  ["date", "Date"],
  ["checkbox", "Case à cocher"],
];
const EMPTY_Q = { label: "", type: "short", options: [], required: false };

function MoveDeleteButtons({ onUp, onDown, onDelete, testPrefix }) {
  return (
    <>
      <button type="button" onClick={onUp} className="text-slate-400 hover:text-slate-700 p-1" title="Monter">
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onDown} className="text-slate-400 hover:text-slate-700 p-1" title="Descendre">
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onDelete} className="text-slate-300 hover:text-red-500 p-1" title="Supprimer"
        data-testid={`${testPrefix}-delete`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </>
  );
}

function QuestionRow({ q, onChange, onMove, onDelete, testPrefix }) {
  return (
    <div className="border border-slate-200 rounded-lg p-2 space-y-1.5 bg-slate-50/60" data-testid={testPrefix}>
      <div className="flex items-center gap-1.5">
        <input value={q.label} onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Question (ex : Nom complet, Courriel, Adresse…)"
          className={inputCls} data-testid={`${testPrefix}-label`} />
        <select value={q.type} onChange={(e) => onChange({ type: e.target.value })}
          className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white flex-shrink-0"
          data-testid={`${testPrefix}-type`}>
          {QUESTION_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <MoveDeleteButtons onUp={() => onMove(-1)} onDown={() => onMove(1)} onDelete={onDelete} testPrefix={testPrefix} />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={!!q.required} onChange={(e) => onChange({ required: e.target.checked })}
            className="accent-blue-900" data-testid={`${testPrefix}-required`} />
          Réponse obligatoire
        </label>
        {q.type === "choice" && (
          <input value={(q.options || []).join(" | ")}
            onChange={(e) => onChange({ options: e.target.value.split("|").map((o) => o.trimStart()) })}
            placeholder="Options séparées par | (ex : Oui | Non | Peut-être)"
            className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-2.5 py-1 text-xs"
            data-testid={`${testPrefix}-options`} />
        )}
      </div>
    </div>
  );
}

function SectionEditor({ s, i, onChange, onMove, onDelete }) {
  const isText = s.type === "text";
  const qs = s.questions || [];
  const setQ = (j, patch) => onChange({ questions: qs.map((q, k) => (k === j ? { ...q, ...patch } : q)) });
  const moveQ = (j, dir) => {
    const arr = [...qs]; const k = j + dir;
    if (k < 0 || k >= arr.length) return;
    [arr[j], arr[k]] = [arr[k], arr[j]];
    onChange({ questions: arr });
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-2.5 space-y-2" data-testid={`qn-section-${i}`}>
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] font-bold px-2 py-1 rounded-md inline-flex items-center gap-1 flex-shrink-0 ${isText ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
          {isText ? <AlignLeft className="w-3 h-3" /> : <ListChecks className="w-3 h-3" />}
          {isText ? "Texte" : "Questions"}
        </span>
        <input value={s.heading} onChange={(e) => onChange({ heading: e.target.value })}
          placeholder="Titre de la section (ex : 1. VOS COORDONNÉES)"
          className={`${inputCls} font-semibold`} data-testid={`qn-section-heading-${i}`} />
        <MoveDeleteButtons onUp={() => onMove(-1)} onDown={() => onMove(1)} onDelete={onDelete} testPrefix={`qn-section-${i}`} />
      </div>
      {isText ? (
        <textarea rows={5} value={s.body} onChange={(e) => onChange({ body: e.target.value })}
          placeholder={"Texte à lire par le client (ex : entente de confidentialité). « • » en début de ligne = puce, **texte** = gras."}
          className={inputCls} data-testid={`qn-section-body-${i}`} />
      ) : (
        <div className="space-y-1.5">
          {qs.map((q, j) => (
            <QuestionRow key={j} q={q} onChange={(p) => setQ(j, p)} onMove={(d) => moveQ(j, d)}
              onDelete={() => onChange({ questions: qs.filter((_, k) => k !== j) })}
              testPrefix={`qn-question-${i}-${j}`} />
          ))}
          <button type="button" onClick={() => onChange({ questions: [...qs, { ...EMPTY_Q }] })}
            className="text-xs text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"
            data-testid={`qn-add-question-${i}`}>
            <Plus className="w-3.5 h-3.5" /> Ajouter une question
          </button>
        </div>
      )}
    </div>
  );
}

function TemplateBar({ value, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const load = () => axios.get(`${API}/admin/questionnaire-templates`, { headers: authH() })
    .then((r) => setTemplates(r.data?.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = (id) => {
    setSelected(id);
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    onApply({ title: tpl.title, sections: tpl.sections });
    setName(tpl.name);
    toast.success(`Modèle « ${tpl.name} » appliqué`);
  };
  const save = async () => {
    if (!name.trim()) { toast.error("Donnez un nom au modèle (ex : Questionnaire nouveau client)"); return; }
    try {
      const r = await axios.post(`${API}/admin/questionnaire-templates`,
        { name: name.trim(), title: value.title, sections: value.sections }, { headers: authH() });
      toast.success(r.data.updated ? `Modèle « ${name} » mis à jour` : `Modèle « ${name} » sauvegardé`);
      load(); setSelected(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde du modèle"); }
  };
  const remove = async () => {
    const tpl = templates.find((t) => t.id === selected);
    if (!tpl || !window.confirm(`Supprimer le modèle « ${tpl.name} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/questionnaire-templates/${tpl.id}`, { headers: authH() });
      toast.message("Modèle supprimé"); setSelected(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-2.5 space-y-2">
      <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
        <FolderOpen className="w-3.5 h-3.5 text-violet-700" /> Mes modèles de questionnaire
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <select value={selected} onChange={(e) => apply(e.target.value)}
          className="flex-1 min-w-[180px] border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="qn-template-select">
          <option value="">— Partir d'un modèle sauvegardé —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {selected && (
          <button type="button" onClick={remove} className="text-slate-300 hover:text-red-500 p-1.5" title="Supprimer ce modèle" data-testid="qn-template-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du modèle (ex : Questionnaire nouveau client)"
          className="flex-1 border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm" data-testid="qn-template-name" />
        <button type="button" onClick={save}
          className="px-3 py-1.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-xs font-semibold inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid="qn-template-save">
          <Save className="w-3.5 h-3.5" /> Sauvegarder comme modèle
        </button>
      </div>
    </div>
  );
}

export function QuestionnaireEditor({ value, onChange }) {
  const v = value;
  const set = (patch) => onChange({ ...v, ...patch });
  const setSection = (i, patch) => set({ sections: v.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const moveSection = (i, dir) => {
    const arr = [...v.sections]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set({ sections: arr });
  };
  const addSection = (type) => set({
    sections: [...v.sections, { type, heading: "", body: "", questions: type === "questions" ? [{ ...EMPTY_Q }] : [] }],
  });

  return (
    <div className="bg-violet-50/50 border border-violet-200 rounded-xl p-3 space-y-3" data-testid="questionnaire-editor">
      <TemplateBar value={v} onApply={(p) => set(p)} />
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Titre du questionnaire (page couverture)</label>
          <input value={v.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} data-testid="qn-title" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Entreprise du client (page couverture — facultatif)</label>
          <input value={v.client_company} onChange={(e) => set({ client_company: e.target.value })} className={inputCls} data-testid="qn-client-company" />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-slate-700">Sections (tout est modifiable) :</p>
        {v.sections.map((s, i) => (
          <SectionEditor key={i} s={s} i={i} onChange={(p) => setSection(i, p)} onMove={(d) => moveSection(i, d)}
            onDelete={() => set({ sections: v.sections.filter((_, j) => j !== i) })} />
        ))}
        <div className="grid sm:grid-cols-2 gap-2">
          <button type="button" onClick={() => addSection("questions")}
            className="text-sm text-blue-800 border border-dashed border-blue-300 rounded-xl px-3 py-2 hover:bg-white inline-flex items-center justify-center gap-1.5"
            data-testid="qn-add-section-questions">
            <ListChecks className="w-4 h-4" /> Ajouter une section Questions
          </button>
          <button type="button" onClick={() => addSection("text")}
            className="text-sm text-amber-800 border border-dashed border-amber-300 rounded-xl px-3 py-2 hover:bg-white inline-flex items-center justify-center gap-1.5"
            data-testid="qn-add-section-text">
            <AlignLeft className="w-4 h-4" /> Ajouter une section Texte
          </button>
        </div>
      </div>
      <p className="text-[10px] text-slate-400">
        Le client répond en ligne puis signe — la date est inscrite automatiquement. Les modèles n'incluent jamais le client.
      </p>
    </div>
  );
}
